import { useQueryClient } from 'react-query';
import { useState } from 'react';

import { Profile, QUERY_KEYS } from '@24i/nxg-sdk-photon';
import { log } from '@24i/nxg-core-utils/src/logger';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';

import { useUserData } from '../../../context/UserData';
import { FetchParams, FetchResult } from '../types';
import { handleError } from '../../../utils/error';

export const useUserProfilesFetch = (): FetchResult<Profile[]> => {
    const queryClient = useQueryClient();
    const { fetchUserProfiles } = useUserData();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);
    const profilesEnabled = useFeature('accounts')?.select_profiles || false;

    const fetch = async (params?: FetchParams): Promise<Profile[]> => {
        if (!profilesEnabled) {
            return [];
        }
        try {
            setIsLoading(true);
            setError(undefined);

            if (params?.forceRefresh) {
                await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.profiles] });
            }

            return await queryClient.fetchQuery({
                queryKey: QUERY_KEYS.profiles,
                queryFn: fetchUserProfiles,
            });
        } catch (e) {
            const err = handleError(e, 'Failed to fetch user profile data');
            setError(err);
            log('Error fetching user data: ', err);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    return { fetch, isLoading, error };
};
